import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, Printer, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

const num = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const printHistory = (d) => {
  const v = d.vehicle;
  const tr = d.periods.map((p, i) => `<tr><td>${i + 1}</td><td>${p.employee_name || "—"}</td><td>${p.from}</td><td>${p.to || "حتى الآن"}</td><td>${p.days ?? ""}</td><td>${p.fuel_count}</td><td>${num(p.fuel_liters)}</td><td>${num(p.fuel_amount)}</td></tr>`).join("");
  const extra = d.unassigned.fuel_count ? `<tr><td></td><td colspan="4">خارج فترات الاستلام</td><td>${d.unassigned.fuel_count}</td><td>${num(d.unassigned.fuel_liters)}</td><td>${num(d.unassigned.fuel_amount)}</td></tr>` : "";
  const w = window.open("", "_blank");
  if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
  w.document.write(`<html dir="rtl"><head><title>كشف تنقل المركبة</title><style>body{font-family:Tahoma,Arial;padding:24px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #cbd5e1;padding:6px;text-align:center}th{background:#f1f5f9}tfoot td{font-weight:bold;background:#f8fafc}</style></head><body>
  <h2>كشف تنقل المركبة</h2><p>اللوحة: <b>${v.identifier}</b> ${v.plate_en ? `(${v.plate_en})` : ""} — النوع: ${v.type}</p>
  <table><thead><tr><th>#</th><th>المستلم</th><th>من تاريخ</th><th>إلى تاريخ</th><th>الأيام</th><th>التعبئات</th><th>اللترات</th><th>البنزين (ريال)</th></tr></thead>
  <tbody>${tr}${extra}</tbody><tfoot><tr><td colspan="5">الإجمالي</td><td>${d.total.fuel_count}</td><td>${num(d.total.fuel_liters)}</td><td>${num(d.total.fuel_amount)}</td></tr></tfoot></table>
  <script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
};

const HistoryTable = ({ d }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="w-full text-sm text-right" data-testid="vehicle-history-table">
      <thead className="bg-slate-50 text-xs text-slate-500">
        <tr>{["المستلم", "البنزين", "اللترات", "التعبئات", "من تاريخ", "إلى تاريخ", "الأيام"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {d.periods.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="vehicle-history-empty">لا يوجد سجل تسليم لهذه المركبة</td></tr>}
        {d.periods.map((p, i) => (
          <tr key={i} data-testid={`vehicle-history-row-${i}`} className={p.current ? "bg-blue-50/40" : ""}>
            <td className="px-3 py-2 font-semibold text-slate-800">{p.employee_name || "—"}{p.current && <span className="ms-1 rounded-full bg-blue-100 px-1.5 text-[10px] text-blue-700">الحالي</span>}</td>
            <td className="px-3 py-2 font-mono-nums font-bold text-emerald-700" data-testid={`vehicle-history-fuel-${i}`}>{num(p.fuel_amount)}</td>
            <td className="px-3 py-2 font-mono-nums">{num(p.fuel_liters)}</td>
            <td className="px-3 py-2 font-mono-nums">{p.fuel_count}</td>
            <td className="px-3 py-2 font-mono-nums whitespace-nowrap" dir="ltr">{p.from}</td>
            <td className="px-3 py-2 font-mono-nums whitespace-nowrap" dir="ltr">{p.to || "حتى الآن"}</td>
            <td className="px-3 py-2 font-mono-nums">{p.days ?? "—"}</td>
          </tr>
        ))}
        {d.unassigned.fuel_count > 0 && (
          <tr className="bg-amber-50/60" data-testid="vehicle-history-unassigned">
            <td className="px-3 py-2 text-amber-800">خارج فترات الاستلام</td>
            <td className="px-3 py-2 font-mono-nums font-bold text-amber-700">{num(d.unassigned.fuel_amount)}</td>
            <td className="px-3 py-2 font-mono-nums">{num(d.unassigned.fuel_liters)}</td>
            <td className="px-3 py-2 font-mono-nums">{d.unassigned.fuel_count}</td>
            <td colSpan={3}></td>
          </tr>
        )}
      </tbody>
      <tfoot className="bg-slate-50 font-bold">
        <tr data-testid="vehicle-history-total">
          <td className="px-3 py-2">الإجمالي</td>
          <td className="px-3 py-2 font-mono-nums text-emerald-700">{num(d.total.fuel_amount)}</td>
          <td className="px-3 py-2 font-mono-nums">{num(d.total.fuel_liters)}</td>
          <td className="px-3 py-2 font-mono-nums">{d.total.fuel_count}</td>
          <td colSpan={3}></td>
        </tr>
      </tfoot>
    </table>
  </div>
);

export const VehicleHistoryDialog = ({ vehicle, onClose }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!vehicle) return;
    setD(null);
    api.vehicleHistory(vehicle.id).then(setD).catch((e) => { toast.error(e?.response?.data?.detail || "تعذّر تحميل الكشف"); onClose(); });
  }, [vehicle]); // eslint-disable-line
  const exportXlsx = async () => {
    setBusy(true);
    try { await saveBlob(await api.vehicleHistoryExport(vehicle.id), `كشف_تنقل_${vehicle.identifier || ""}.xlsx`); }
    catch { toast.error("تعذّر التصدير"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!vehicle} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="vehicle-history-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-blue-600" /> كشف تنقل المركبة <span className="text-slate-500 text-base">{vehicle?.identifier}</span>{vehicle?.plate_en && <span dir="ltr" className="text-slate-400 text-sm font-mono">{vehicle.plate_en}</span>}</DialogTitle>
          <DialogDescription>من استلم المركبة ومتى، وكم صرف بنزين خلال كل فترة (حسب عمليات اللوحة).</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : (
          <>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => printHistory(d)} data-testid="vehicle-history-print"><Printer className="h-4 w-4" /> طباعة</Button>
              <Button size="sm" variant="outline" className="gap-1.5 border-emerald-200 text-emerald-700" disabled={busy} onClick={exportXlsx} data-testid="vehicle-history-export">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير Excel
              </Button>
            </div>
            <HistoryTable d={d} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
